<template>
  <Teleport to="body">
    <div
      v-if="attachment"
      class="modal-backdrop attachment-preview-backdrop"
      role="presentation"
      @click.self="emit('close')"
    >
      <section class="modal attachment-preview-modal" role="dialog" aria-modal="true" :aria-label="`预览 ${attachment.filename}`">
        <header>
          <div class="attachment-preview-heading">
            <span class="eyebrow">附件预览</span>
            <h3>{{ attachment.filename }}</h3>
            <span>{{ formatBytes(attachment.size_bytes) }}</span>
          </div>
          <button class="icon-button" title="关闭" aria-label="关闭附件预览" @click="emit('close')">
            <X :size="18" />
          </button>
        </header>

        <div class="attachment-preview-body">
          <div v-if="loading" class="attachment-preview-state" role="status">
            <LoaderCircle class="spinning" :size="28" />
            <span>正在加载附件…</span>
          </div>
          <div v-else-if="errorMessage" class="attachment-preview-state">
            <FileWarning :size="34" />
            <strong>附件加载失败</strong>
            <span>{{ errorMessage }}</span>
            <button type="button" @click="loadAttachment"><RotateCcw :size="16" />重试</button>
          </div>
          <img
            v-else-if="previewKind === 'image' && !renderFailed"
            class="attachment-preview-image"
            :src="objectUrl"
            :alt="attachment.filename"
            @error="renderFailed = true"
          />
          <iframe
            v-else-if="previewKind === 'pdf'"
            class="attachment-preview-frame"
            :src="objectUrl"
            :title="attachment.filename"
          />
          <div v-else-if="previewKind === 'text'" class="attachment-preview-text-wrap">
            <pre class="attachment-preview-text">{{ textContent }}</pre>
            <p v-if="textTruncated" class="attachment-preview-note">文件较大，仅显示前 2 MiB；可下载后查看完整内容。</p>
          </div>
          <div v-else class="attachment-preview-state">
            <File :size="42" />
            <strong>{{ renderFailed ? "浏览器无法显示这张图片" : "此类型不支持站内预览" }}</strong>
            <span>可以在新标签页中打开，或下载后使用本地应用查看。</span>
          </div>
        </div>

        <footer class="modal-actions attachment-preview-actions">
          <button
            v-if="previewKind === 'pdf' || previewKind === 'unsupported' || renderFailed"
            type="button"
            :disabled="!objectUrl"
            @click="openExternal"
          >
            <ExternalLink :size="17" />新标签页打开
          </button>
          <button type="button" :disabled="!objectUrl" @click="download">
            <Download :size="17" />下载
          </button>
          <button type="button" class="primary" @click="emit('close')">关闭</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Download, ExternalLink, File, FileWarning, LoaderCircle, RotateCcw, X } from "@lucide/vue";
import { api } from "../api";
import { formatBytes, readableError } from "../composables/useFormat";
import type { Attachment } from "../types";

const TEXT_PREVIEW_LIMIT = 2 * 1024 * 1024;
const props = defineProps<{ attachment: Attachment | null }>();
const emit = defineEmits<{ close: [] }>();
const loading = ref(false);
const errorMessage = ref("");
const objectUrl = ref("");
const textContent = ref("");
const textTruncated = ref(false);
const renderFailed = ref(false);
let loadVersion = 0;

const previewKind = computed<"image" | "pdf" | "text" | "unsupported">(() => {
  const mime = props.attachment?.mime_type || "";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("text/")) return "text";
  return "unsupported";
});

watch(() => props.attachment?.id, loadAttachment, { immediate: true });
onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  clearObjectUrl();
});

async function loadAttachment() {
  const attachment = props.attachment;
  loadVersion += 1;
  const currentVersion = loadVersion;
  clearObjectUrl();
  errorMessage.value = "";
  textContent.value = "";
  textTruncated.value = false;
  renderFailed.value = false;
  if (!attachment) {
    loading.value = false;
    return;
  }

  loading.value = true;
  try {
    const blob = await api.attachmentBlob(attachment.id);
    if (currentVersion !== loadVersion || props.attachment?.id !== attachment.id) return;
    objectUrl.value = URL.createObjectURL(blob);
    if (previewKind.value === "text") {
      textContent.value = await blob.slice(0, TEXT_PREVIEW_LIMIT).text();
      textTruncated.value = blob.size > TEXT_PREVIEW_LIMIT;
    }
  } catch (error) {
    if (currentVersion === loadVersion) errorMessage.value = readableError(error);
  } finally {
    if (currentVersion === loadVersion) loading.value = false;
  }
}

function clearObjectUrl() {
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = "";
}

function openExternal() {
  if (!objectUrl.value) return;
  window.open(objectUrl.value, "_blank", "noopener,noreferrer");
}

function download() {
  if (!objectUrl.value || !props.attachment) return;
  const link = document.createElement("a");
  link.href = objectUrl.value;
  link.download = props.attachment.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && props.attachment) {
    event.stopImmediatePropagation();
    emit("close");
  }
}
</script>
